import {
  Acabado,
  CalcularPreciosData,
  Unidad,
  usePreciosHubContext,
} from '@context/PreciosHubContext'
import { useAppStore } from '@store'
import { useCallback, useEffect, useMemo, useRef } from 'react'

type Texture = { linkedTextureID?: number; searchID?: number }
type PriceItem = {
  id: string
  idUnidades: number
  quantity?: number
  width?: number
  height?: number
  thickness?: number
  textures?: Texture[]
  childrenOrder?: string[]
  type: string
  firstParentID?: string
}

function formatTopbarPrice(num: number) {
  const rounded = Math.round(Number(num) || 0)
  return rounded.toLocaleString('es-ES', {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  })
}

/**
 * Recalcula y expone el precio total del diseño (misma lógica que Topbar desktop).
 * Necesario en CompactTopbar: en touch no se monta Topbar y el total no se actualizaría.
 */
export function useTopbarPrecioTotal() {
  const { calcularPrecios, resultadoPrecios, isConnected } = usePreciosHubContext()
  const DarTotalLineas = true
  const DarTotalOtros = true
  const itemsMap = useAppStore.use.items()
  const debounceTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)

  const priceRecalcKey = useAppStore((store) => {
    const relevantItems = Array.from(store.items.values()).filter(
      (sceneItem: any) =>
        sceneItem?.type === 'linkedItem' ||
        sceneItem?.type === 'items' ||
        sceneItem?.type === 'walls',
    )
    return relevantItems
      .map((sceneItem: any) => {
        const textureKey = (sceneItem?.textures || [])
          .map(
            (tex: any) =>
              `${tex?.zoneName ?? ''}:${tex?.searchID ?? 0}:${tex?.linkedTextureID ?? 0}`,
          )
          .join(',')
        return [
          sceneItem?.id ?? '',
          sceneItem?.idUnidades ?? 0,
          sceneItem?.quantity ?? 1,
          sceneItem?.width ?? 0,
          sceneItem?.height ?? 0,
          sceneItem?.thickness ?? 0,
          textureKey,
        ].join(':')
      })
      .sort()
      .join('|')
  })

  const mapAcabados = useCallback((textures: Texture[] = []): Acabado[] => {
    if (!textures) return []
    return textures.map((tex) => ({
      IdAcabadoUnidad: tex.linkedTextureID ?? 0,
      IdTexturas: tex.searchID ?? 0,
    }))
  }, [])

  const mapUnidad = useCallback(
    (item: PriceItem, liveItemsMap: Map<string, PriceItem>): Unidad => {
      const enlaces: Unidad[] = Array.from(liveItemsMap.entries())
        .filter(([, child]) => child.firstParentID === item.id)
        .map(([, child]) => ({
          IdFEUnidad: child.id,
          IdUnidades: child.idUnidades ?? 0,
          Cantidad: child.quantity ?? 1,
          Ancho: child.width ?? 0,
          Alto: child.height ?? 0,
          Fondo: child.thickness ?? 0,
          Acabados: mapAcabados(child.textures),
          Enlaces: [],
        }))

      return {
        IdFEUnidad: item.id,
        IdUnidades: item.idUnidades,
        Cantidad: item.quantity ?? 1,
        Ancho: item.width,
        Alto: item.height,
        Fondo: item.thickness,
        Acabados: mapAcabados(item.textures),
        Enlaces: enlaces,
      }
    },
    [mapAcabados],
  )

  const priceUnitsCount = useMemo(() => {
    return Array.from(itemsMap.values()).filter(
      (item: PriceItem) =>
        item.type === 'linkedItem' || item.type === 'items' || item.type === 'walls',
    ).length
  }, [itemsMap])

  const precioTotalValue =
    priceUnitsCount <= 0
      ? 0
      : typeof resultadoPrecios?.precioTotal === 'number'
        ? resultadoPrecios.precioTotal
        : 0

  useEffect(() => {
    if (!isConnected) return

    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current)
    }

    debounceTimeout.current = setTimeout(() => {
      const liveItemsMap = useAppStore.getState().items as unknown as Map<string, PriceItem>
      const unidades: Unidad[] = Array.from(liveItemsMap.values())
        .filter(
          (item: PriceItem) =>
            item.type === 'linkedItem' || item.type === 'items' || item.type === 'walls',
        )
        .map((item: PriceItem) => mapUnidad(item, liveItemsMap))

      const data: CalcularPreciosData = {
        Unidades: unidades,
        DarTotalLineas,
        DarTotalOtros,
      }
      calcularPrecios(data).catch(() => {
        if (unidades.length === 0) {
          return
        }
      })
    }, 400)

    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current)
      }
    }
  }, [
    priceRecalcKey,
    priceUnitsCount,
    DarTotalLineas,
    DarTotalOtros,
    mapUnidad,
    isConnected,
    calcularPrecios,
  ])

  return {
    precioTotalValue,
    precioTotalFormatted: formatTopbarPrice(precioTotalValue),
    formatNumber: formatTopbarPrice,
  }
}
